import type { AiPreviewScreen } from '@/features/campaigns/types/campaign';

export type AiPlanTotals = {
  subtotal: number;
  tax: number;
  discount: number;
  total: number;
};

export type AiPlanEstimate = AiPlanTotals & {
  /** How much cheaper the order is thanks to the coupon, GST included (0 without one). */
  couponSavings: number;
  budget: number | null;
  remaining: number | null;
  overBudget: boolean;
};

export const aiScreenKey = (s: AiPreviewScreen) => String(s._id || s.screenName || '');

/** Price of one play on one day for a screen, from the plan's own per-screen pricing. */
export function perPlayPrice(screen: AiPreviewScreen) {
  const reps = Number(screen.pricing?.totalRepetitions ?? 0);
  if (reps <= 0) return { subtotal: 0, tax: 0, gross: 0 };
  const subtotal = Number(screen.pricing?.amountWithoutTax ?? 0) / reps;
  const tax = Number(screen.pricing?.totalTax ?? 0) / reps;
  return { subtotal, tax, gross: subtotal + tax };
}

/**
 * Prices the plan for the screens the user kept and the plays per day they chose, using each
 * screen's own subtotal and GST per play. Any coupon discount shrinks with the subtotal.
 */
export function estimateAiPlan({
  screens,
  selectedKeys,
  playsByKey,
  days,
  discountFor,
  budget,
}: {
  screens: AiPreviewScreen[];
  selectedKeys: Set<string>;
  playsByKey: Record<string, number>;
  days: number;
  /** The coupon's discount on an order of this many rupees before GST (0 when none is applied). */
  discountFor: (base: number) => number;
  budget: number | null;
}): AiPlanEstimate {
  let subtotal = 0;
  let tax = 0;
  for (const screen of screens) {
    const key = aiScreenKey(screen);
    if (!selectedKeys.has(key)) continue;
    const price = perPlayPrice(screen);
    const plays = (playsByKey[key] ?? 0) * days;
    subtotal += price.subtotal * plays;
    tax += price.tax * plays;
  }
  // A coupon takes money off the amount before GST, and GST is then charged on what is left, so
  // the tax shrinks by the same share (this is how the server prices it).
  const discount = Math.min(subtotal, Math.max(0, discountFor(subtotal)));
  const taxAfterDiscount = subtotal > 0 ? tax * ((subtotal - discount) / subtotal) : tax;
  const total = Math.max(0, subtotal - discount + taxAfterDiscount);
  const taxBeforeDiscount = subtotal > discount ? taxAfterDiscount * (subtotal / (subtotal - discount)) : tax;
  const couponSavings = discount + (taxBeforeDiscount - taxAfterDiscount);

  return {
    subtotal,
    tax: taxAfterDiscount,
    discount,
    total,
    couponSavings,
    budget,
    remaining: budget != null ? budget - total : null,
    overBudget: budget != null && total > budget + 0.005,
  };
}

/** The most plays per day a screen can take without the total going past the budget. */
export function maxPlaysPerDay({
  screen,
  currentPlays,
  currentTotal,
  days,
  budget,
  capacityCap,
}: {
  screen: AiPreviewScreen;
  /** Free plays a day the screen has in total, when known. */
  capacityCap?: number;
  currentPlays: number;
  currentTotal: number;
  days: number;
  budget: number | null;
}) {
  if (budget == null) return capacityCap ?? Number.POSITIVE_INFINITY;
  const grossPerPlayDay = perPlayPrice(screen).gross * days;
  if (grossPerPlayDay <= 0) return currentPlays;
  const headroom = budget - currentTotal;
  const byBudget = currentPlays + Math.max(0, Math.floor((headroom + 0.005) / grossPerPlayDay));
  return Math.min(byBudget, capacityCap ?? Number.POSITIVE_INFINITY);
}
